import { george, i18n, observer, pr, RouteComponentProps, theme, ui } from "aaa";
import { NativeRaisedButton } from "components/common/NativeRaisedButton";
import * as React from "react";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { pushRoute } from "stores/RouterFunctions";
import { getDefaultKeyPressHandler } from "util/helpers";
import { JuniorRoutes } from "../JuniorRoutes";
import { JuniorStore } from "../JuniorStore";

type IProps = RouteComponentProps<{}> & i18n.InjectedIntlProps & {
    store: JuniorStore
};

const ACTIVATION_STEPS = [
    {
        image: require("../../../assets/gJunior/junior_tour_step1.svg"),
        title: "gJunior.activate.step1.title",
        description: "gJunior.activate.step1.description",
        step: 1,
    },
    {
        image: require("../../../assets/gJunior/junior_tour_step2.svg"),
        title: "gJunior.activate.step2.title",
        description: "gJunior.activate.step2.description",
        step: 2,
    },
    {
        image: require("../../../assets/gJunior/junior_tour_step3.svg"),
        title: "gJunior.activate.step3.title",
        description: "gJunior.activate.step3.description",
        step: 3,
    },
    {
        image: require("../../../assets/gJunior/junior_tour_step4.svg"),
        title: "gJunior.activate.step4.title",
        description: "gJunior.activate.step4.description",
        step: 4,
    },
];

export const JuniorActivateTour = observer(({ store }: IProps) => {
    const isMobile = externalInterfaceStore.isGeorgeApp || ui.windowWidth < 600;
    const ref = React.useRef<HTMLAnchorElement | null>(null);
    const [currentStep, setCurrentStep] = React.useState(0);

    const juniorValue = store.selectedChild?.juniorValue;

    const BackgroundComponent = externalInterfaceStore.isGeorgeApp ? george.Background : george.BackgroundWithNavBar;

    const stepData = ACTIVATION_STEPS[currentStep];
    const isLastStep = currentStep === ACTIVATION_STEPS.length - 1;
    const skipButtonhidden = currentStep === 0 || currentStep === 3;

    const handleBack = () => {
        if (currentStep === 0) {
            pushRoute(JuniorRoutes.DETAIL);
        } else {
            setCurrentStep(prev => prev - 1);
        }
    }

    const handleActivate = async () => {
        if (juniorValue === "migrationNeeded") {
            try {
                await store.activateJunior();
                pushRoute(JuniorRoutes.ACTIVATE_SIGNING);
            } catch (error) {
                pushRoute(JuniorRoutes.ACTIVATE_ERROR);
            }
        } else if (juniorValue === "activationNeeded") {
            pushRoute(JuniorRoutes.ACTIVATE_CONTACT);
        } else {
            return;
        }
    }

    const handleNext = () => {
        if (isLastStep) {
            handleActivate();
        } else {
            setCurrentStep(prev => prev + 1);
        }
    }

    const handleSkip = () => {
        setCurrentStep(3);
    }

    if (store.isLoading) {
        return (
            <BackgroundComponent>
                <george.LoadingIndicator isLoading={store.isLoading} height={ui.windowHeight} />
            </BackgroundComponent>
        );
    }

    const isDark = ui.isDarkMode;
    const tourSurfaceColor = isDark ? theme.themeColors.backgroundColorDark : theme.themeColors.georgeBlue;
    const stepDotInactive = isDark ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.4)";

    return (
        <BackgroundComponent style={{ display: "flex", flexDirection: "column" }}>
            <div style={{
                backgroundColor: tourSurfaceColor,
                padding: theme.MARGIN,
                zIndex: 1,
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                justifyContent: "center",
            }}>
                <pr.A11yPopupHider>
                    <a
                        ref={ref}
                        style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", flexGrow: 1, flexBasis: 0 }}
                        role="button"
                        tabIndex={0}
                        onClick={handleBack}
                        onKeyPress={getDefaultKeyPressHandler(handleBack)}
                        aria-label={ui.__("a11y.back")}
                    >
                        <pr.IconChevronLeft style={{ display: "block", flexShrink: 0, color: "white" }} />
                        {!isMobile && (
                            <pr.P_16_CONTENT style={{ fontWeight: 600, color: "white" }}>
                                {ui.__("a11y.back")}
                            </pr.P_16_CONTENT>
                        )}
                    </a>
                </pr.A11yPopupHider>

                <div style={{
                    textAlign: "center",
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    marginTop: theme.MARGIN_LARGE,
                }}>
                    <img
                        src={stepData.image}
                        alt={`George Junior Activate Step ${currentStep + 1}`}
                        style={{
                            maxWidth: "100%",
                            height: "auto",
                            maxHeight: 256,
                            borderRadius: 8,
                        }}
                    />
                    <pr.LandingPageTitle style={{
                        color: "white",
                        margin: `${theme.MARGIN} 0`,
                        fontSize: 24,
                        lineHeight: "32px",
                        fontWeight: 700
                    }}>
                        {ui.__(stepData.title as i18n.IDS)}
                    </pr.LandingPageTitle>

                    <pr.P_16_CONTENT style={{
                        color: "white",
                        marginBottom: theme.MARGIN,
                        opacity: 0.9
                    }}>
                        {ui.__(stepData.description as i18n.IDS)}
                    </pr.P_16_CONTENT>
                </div>
                <div style={{
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    gap: theme.MARGIN,
                }}>
                    {/* Step indicators */}
                    <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
                        {ACTIVATION_STEPS.map((_, index) => (
                            <div
                                key={index}
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor: index === currentStep ? "white" : stepDotInactive,
                                    transition: "all 0.3s ease"
                                }}
                            />
                        ))}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", justifyContent: "start" }}>
                        <NativeRaisedButton
                            style={{
                                height: 56,
                                boxShadow: "none",
                                borderRadius: theme.BORDER_RADIUS_FULL,
                                backgroundColor: "#ffffff",
                            }}
                            fullWidth={isMobile}
                            primary
                            labelStyle={{ color: theme.themeColors.georgeBlue, fontSize: 18, fontWeight: 600 }}
                            label={ui.__(isLastStep ? "gJunior.activate.stepper.activate" : "gJunior.activate.stepper.next")}
                            onClick={handleNext}
                        />

                        <a
                            style={{
                                color: "white",
                                cursor: "pointer",
                                height: 56,
                                fontSize: 18,
                                lineHeight: "24px",
                                fontWeight: 600,
                                visibility: skipButtonhidden ? "hidden" : "visible",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                            role="button"
                            tabIndex={0}
                            onClick={handleSkip}
                            onKeyPress={getDefaultKeyPressHandler(handleSkip)}
                        >
                            {ui.__("gJunior.activate.stepper.skip")}
                        </a>
                    </div>
                </div>
            </div>
        </BackgroundComponent >
    );
}); 


// WEBPACK FOOTER //
// ./src/components/gJunior/activateStep/JuniorActivateTour.tsx